import React from 'react'
import axios from 'axios'
import { Paper, TextField, Button, IconButton, Chip, CircularProgress, Typography } from '@material-ui/core'
import {
  Close as CloseIcon,
  Remove as MinimizeIcon,
  AttachFile as AttachIcon,
  DragIndicator as DragIcon,
} from '@material-ui/icons'

// One Quick Capture sticky note. Presentational: all draft state is owned by the
// manager and arrives via props; every change is reported up through onChange(id, patch).
const QuickCaptureCard = ({
  id, body, scopeLabel, scopeProject, attachments, pos, url,
  onChange, onSave, onClose, onMinimize,
}) => {
  const [uploading, setUploading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')
  const fileRef = React.useRef(null)

  // Pointer-based drag on the header — reports the new position up via onChange.
  const onDragStart = (e) => {
    const startX = e.clientX
    const startY = e.clientY
    const origin = { ...pos }
    const onMove = (ev) => {
      onChange(id, {
        pos: {
          x: Math.max(0, origin.x + ev.clientX - startX),
          y: Math.max(0, origin.y + ev.clientY - startY),
        },
      })
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const handleAttach = async (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    setUploading(true)
    setError('')
    try {
      const uploaded = []
      for (const file of files) {
        const presignRes = await axios.post(`${url}/api/quick-capture/presigned-url`, {
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
        })
        const { uploadUrl, publicUrl } = presignRes.data.data
        await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } })
        uploaded.push({
          filename: file.name,
          url: publicUrl,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
        })
      }
      onChange(id, { attachments: [...attachments, ...uploaded] })
    } catch (err) {
      console.error('Attachment upload failed:', err)
      setError('Upload failed — try again or save text only.')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const removeAttachment = (idx) => {
    onChange(id, { attachments: attachments.filter((_, i) => i !== idx) })
  }

  const handleSave = () => {
    if (!body.trim()) { setError('Write something first.'); return }
    setSaving(true)
    setError('')
    axios.post(`${url}/api/quick-capture/captures`, {
      body: body.trim(),
      scopeLabel,
      scopeProject: scopeProject || null,
      attachments,
    }).then(() => {
      setSaving(false)
      onSave(id)
    }).catch(err => {
      setSaving(false)
      console.error('Failed to save capture:', err)
      setError('Save failed — your draft is kept. ' + (err.response?.data?.error || err.message))
    })
  }

  return (
    <Paper
      elevation={8}
      style={{ position: 'fixed', left: pos.x, top: pos.y, width: 340, zIndex: 1500, display: 'flex', flexDirection: 'column' }}
    >
      {/* Drag header */}
      <div
        onPointerDown={onDragStart}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px 6px 12px', background: '#fffde7', borderBottom: '1px solid #f0e6a8', cursor: 'move', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <DragIcon fontSize="small" style={{ color: '#bfae4e' }} />
          <Typography variant="body2" style={{ fontWeight: 600 }}>Quick Capture</Typography>
        </div>
        <div>
          <IconButton size="small" onClick={() => onMinimize(id)} title="Minimize">
            <MinimizeIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onClose(id)} title="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: 12 }}>
        <TextField
          label="Scope"
          fullWidth
          size="small"
          value={scopeLabel}
          onChange={e => onChange(id, { scopeLabel: e.target.value, scopeProject: null })}
          margin="dense"
          placeholder="Project, client, or ticket"
        />
        <TextField
          label="Capture"
          fullWidth
          size="small"
          multiline
          rows={5}
          autoFocus
          value={body}
          onChange={e => { onChange(id, { body: e.target.value }); setError('') }}
          margin="dense"
          placeholder="Jot a thought..."
        />

        {attachments.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
            {attachments.map((a, i) => (
              <Chip key={i} label={a.filename} size="small" onDelete={() => removeAttachment(i)} style={{ maxWidth: 140 }} />
            ))}
          </div>
        )}

        {error && (
          <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 6 }}>{error}</Typography>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
          <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={handleAttach} />
          <Button
            size="small"
            startIcon={uploading ? <CircularProgress size={14} /> : <AttachIcon />}
            disabled={uploading}
            onClick={() => fileRef.current && fileRef.current.click()}
          >
            Attach
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={saving || uploading}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </Paper>
  )
}

export default QuickCaptureCard
